/**
 * MemoHistorySheet - メモ本文の履歴の一覧。選んだ版はメモ画面の本文に表示する（MemoRevisionPreview）
 */

import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
  getTextDocumentRevision,
  getTextDocumentRevisions,
  type TextDocumentRevision,
  type TextDocumentRevisionItem,
} from '@/api/services/memo'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Spinner } from '@/components/ui/spinner'
import { formatRevisionSavedAt, REVISION_REASON_LABEL } from '@/lib/memo-revision'
import { cn } from '@/lib/utils'

type MemoHistorySheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** TextDocument の種別（既定はメモ） */
  entityType?: string
  entityId: number
  /** 表示中の版 */
  selectedId: number | null
  onSelect: (revision: TextDocumentRevision) => void
}

export const MemoHistorySheet = ({
  open,
  onOpenChange,
  entityType = 'memo',
  entityId,
  selectedId,
  onSelect,
}: MemoHistorySheetProps) => {
  const [items, setItems] = useState<TextDocumentRevisionItem[] | null>(null)
  const [loadingId, setLoadingId] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setItems(null)
    getTextDocumentRevisions(entityType, entityId)
      .then((res) => {
        if (!cancelled) setItems(res)
      })
      .catch(() => {
        if (cancelled) return
        setItems([])
        toast.error('履歴を取得できませんでした')
      })
    return () => {
      cancelled = true
    }
  }, [open, entityType, entityId])

  const handleSelect = async (item: TextDocumentRevisionItem) => {
    setLoadingId(item.id)
    try {
      onSelect(await getTextDocumentRevision(entityType, entityId, item.id))
    } catch {
      toast.error('履歴を取得できませんでした')
    } finally {
      setLoadingId(null)
    }
  }

  return (
    // 背後のメモ本文に選んだ版を出すので、オーバーレイで暗くしない
    <BottomSheet title="履歴" open={open} onOpenChange={onOpenChange} snapPoints={[0.45]} noOverlay>
      <div className="px-4 pb-6">
        {items === null ? (
          <div className="flex justify-center py-8">
            <Spinner className="size-6" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">履歴はまだありません</p>
        ) : (
          <ul className="divide-y">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => void handleSelect(item)}
                  disabled={loadingId !== null}
                  aria-current={item.id === selectedId}
                  className={cn(
                    'flex w-full cursor-pointer flex-col gap-1 rounded-md px-2 py-2.5 text-left hover:bg-accent/50',
                    item.id === selectedId && 'bg-accent',
                  )}
                >
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">{formatRevisionSavedAt(item)}</span>
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      {REVISION_REASON_LABEL[item.reason]}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {loadingId === item.id ? <Spinner className="size-3" /> : `${item.text_length}文字`}
                    </span>
                  </div>
                  <p className="line-clamp-2 text-xs text-muted-foreground">{item.preview || '（本文なし）'}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </BottomSheet>
  )
}
